import { useEffect, useMemo } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select } from '@/components/ui/Select'
import { Checkbox } from '@/components/ui/Checkbox'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { cn } from '@/utils/cn'
import { CreateQuizPayload, UpdateQuizPayload } from '@/types/quizzes'

const DEVICE_OPTIONS = [
  { value: 'desktop', label: 'Desktop computer' },
  { value: 'laptop', label: 'Laptop' },
  { value: 'tablet', label: 'Tablet' },
  { value: 'mobile', label: 'Mobile phone' },
]

const quizSchema = z.object({
  courseId: z.string().min(1, 'Please choose a course'),
  title: z.string().min(3, 'Title should be at least 3 characters'),
  description: z.string().optional(),
  isActive: z.boolean().default(true),
  allowDevices: z.array(z.string()).optional(),
  requireProctoring: z.boolean().optional(),
  maxAttempts: z
    .union([z.coerce.number().int().positive(), z.literal('')])
    .optional()
    .transform((value) => (value === '' ? null : Number(value))),
  timeLimitMinutes: z
    .union([z.coerce.number().positive(), z.literal('')])
    .optional()
    .transform((value) => (value === '' ? null : Number(value))),
  dueDate: z
    .string()
    .optional()
    .refine((value) => {
      if (!value) return true
      const date = new Date(value)
      return !Number.isNaN(date.getTime())
    }, 'Please provide a valid date and time'),
  slug: z
    .string()
    .max(120, 'Slug must be 120 characters or less')
    .optional()
    .transform((value) => (value ? value.trim() : undefined)),
})

export type QuizFormValues = z.infer<typeof quizSchema>

export interface QuizFormInitialValues {
  courseId?: string
  title?: string
  description?: string | null
  isActive?: boolean
  allowDevices?: string[] | null
  requireProctoring?: boolean | null
  maxAttempts?: number | null
  timeLimitSeconds?: number | null
  dueDate?: string | null
  slug?: string | null
}

export interface CourseSelectOption {
  id: string
  label: string
  subtitle?: string
}

export interface QuizFormProps {
  courses: CourseSelectOption[]
  initialValues?: QuizFormInitialValues
  submitLabel?: string
  isSubmitting?: boolean
  onSubmit: (payload: CreateQuizPayload | UpdateQuizPayload) => Promise<void> | void
}

const formatDateForInput = (iso?: string | null): string => {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
    date.getMinutes(),
  )}`
}

export function QuizForm({
  courses,
  initialValues,
  submitLabel = 'Save quiz',
  isSubmitting,
  onSubmit,
}: QuizFormProps) {
  const defaultValues = useMemo<QuizFormValues>(
    () => ({
      courseId: initialValues?.courseId ?? '',
      title: initialValues?.title ?? '',
      description: initialValues?.description ?? '',
      isActive: initialValues?.isActive ?? true,
      allowDevices: initialValues?.allowDevices ?? [],
      requireProctoring: initialValues?.requireProctoring ?? false,
      maxAttempts: initialValues?.maxAttempts ?? null,
      timeLimitMinutes: initialValues?.timeLimitSeconds
        ? Math.round(initialValues.timeLimitSeconds / 60)
        : null,
      dueDate: formatDateForInput(initialValues?.dueDate),
      slug: initialValues?.slug ?? undefined,
    }),
    [initialValues],
  )

  const { control, register, handleSubmit, reset, formState: { errors } } = useForm<QuizFormValues>({
    resolver: zodResolver(quizSchema),
    defaultValues,
    mode: 'onBlur',
  })

  useEffect(() => {
    if (!initialValues) return
    reset({
      courseId: initialValues.courseId ?? '',
      title: initialValues.title ?? '',
      description: initialValues.description ?? '',
      isActive: initialValues.isActive ?? true,
      allowDevices: initialValues.allowDevices ?? [],
      requireProctoring: initialValues.requireProctoring ?? false,
      maxAttempts: initialValues.maxAttempts ?? null,
      timeLimitMinutes: initialValues.timeLimitSeconds
        ? Math.round(initialValues.timeLimitSeconds / 60)
        : null,
      dueDate: formatDateForInput(initialValues.dueDate),
      slug: initialValues.slug ?? undefined,
    })
  }, [initialValues, reset])

  const submitHandler = async (values: QuizFormValues) => {
    const allowDevices = values.allowDevices?.length ? values.allowDevices : undefined
    const maxAttempts = values.maxAttempts ?? undefined
    const timeLimitSeconds = values.timeLimitMinutes ? values.timeLimitMinutes * 60 : undefined
    const dueDateIso = values.dueDate ? new Date(values.dueDate).toISOString() : undefined

    const payload: CreateQuizPayload = {
      courseId: values.courseId,
      title: values.title.trim(),
      description: values.description?.trim() || undefined,
      isActive: values.isActive,
      allowDevices,
      requireProctoring: values.requireProctoring ?? false,
      maxAttempts,
      timeLimitSeconds,
      dueDate: dueDateIso,
      slug: values.slug,
    }

    await onSubmit(payload)
  }

  return (
    <form onSubmit={handleSubmit(submitHandler)} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Quiz basics</CardTitle>
          <CardDescription>
            Give your quiz a clear name, connect it to a course, and decide how and when learners
            can access it.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="courseId">Course</Label>
              <Controller
                control={control}
                name="courseId"
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onChange={(event) => field.onChange(event.target.value)}
                  >
                    <option value="">Select course</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.label}
                        {course.subtitle ? ` – ${course.subtitle}` : ''}
                      </option>
                    ))}
                  </Select>
                )}
              />
              {errors.courseId && (
                <p className="text-sm text-red-600">{errors.courseId.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="title">Quiz title</Label>
              <Input
                id="title"
                placeholder="e.g. Marketing Fundamentals Check-in"
                {...register('title')}
              />
              {errors.title && <p className="text-sm text-red-600">{errors.title.message}</p>}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Quiz description</Label>
            <Textarea
              id="description"
              rows={4}
              placeholder="Let learners know what this quiz covers and how to prepare."
              {...register('description')}
            />
            {errors.description && (
              <p className="text-sm text-red-600">{errors.description.message}</p>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="timeLimitMinutes">Time limit (minutes)</Label>
              <Input
                id="timeLimitMinutes"
                type="number"
                min={1}
                placeholder="Leave blank for no timer"
                {...register('timeLimitMinutes')}
              />
              {errors.timeLimitMinutes && (
                <p className="text-sm text-red-600">{errors.timeLimitMinutes.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="maxAttempts">Maximum attempts</Label>
              <Input
                id="maxAttempts"
                type="number"
                min={1}
                placeholder="Default is single attempt"
                {...register('maxAttempts')}
              />
              {errors.maxAttempts && (
                <p className="text-sm text-red-600">{errors.maxAttempts.message}</p>
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="dueDate">Due date (optional)</Label>
              <Input id="dueDate" type="datetime-local" {...register('dueDate')} />
              {errors.dueDate && <p className="text-sm text-red-600">{errors.dueDate.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="slug">Friendly link (optional)</Label>
              <Input
                id="slug"
                placeholder="microeconomics-midterm"
                {...register('slug')}
              />
              {errors.slug && <p className="text-sm text-red-600">{errors.slug.message}</p>}
              <p className="text-xs text-gray-500">
                Use lowercase letters, numbers, or dashes to create an easier link.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Access settings</CardTitle>
          <CardDescription>
            Decide whether the quiz is visible, if proctoring is required, and which devices are
            permitted.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-1.5">
            <Checkbox {...register('isActive')} label="Make quiz available right away" />
            <p className="text-sm text-gray-500">
              Learners will see this quiz as soon as it is published.
            </p>
          </div>

          <div className="space-y-1.5">
            <Checkbox {...register('requireProctoring')} label="Require live supervision" />
            <p className="text-sm text-gray-500">
              Enable this if learners must complete the quiz under staff supervision.
            </p>
          </div>

          <Controller
            control={control}
            name="allowDevices"
            render={({ field }) => {
              const selected: string[] = Array.isArray(field.value) ? field.value : []
              return (
                <div>
                  <Label className="font-medium">Allowed devices</Label>
                  <p className="text-sm text-gray-500">
                    Select the devices that provide the best experience for this quiz.
                  </p>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    {DEVICE_OPTIONS.map((device) => {
                      const isSelected = selected.includes(device.value)
                      return (
                        <label
                          key={device.value}
                          className={cn(
                            'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors',
                            isSelected
                              ? 'border-primary bg-primary/5'
                              : 'border-gray-200 hover:border-primary/60',
                          )}
                        >
                          <input
                            type="checkbox"
                            value={device.value}
                            checked={isSelected}
                            onChange={(event) => {
                              const next = event.target.checked
                                ? [...selected, device.value]
                                : selected.filter((value) => value !== device.value)
                              field.onChange(Array.from(new Set(next)))
                            }}
                            className="mt-1"
                            aria-label={device.label}
                          />
                          <div>
                            <span className="font-medium text-sm">{device.label}</span>
                            <p className="text-xs text-gray-500">
                              {device.value === 'desktop'
                                ? 'Best for longer exams with detailed content.'
                                : device.value === 'laptop'
                                  ? 'Flexible option for most learners.'
                                  : device.value === 'tablet'
                                    ? 'Good for responsive quizzes.'
                                    : 'Mobile experience works best for short quizzes.'}
                            </p>
                          </div>
                        </label>
                      )
                    })}
                  </div>
                </div>
              )
            }}
          />
        </CardContent>
      </Card>

      <div className="flex justify-end gap-3">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : submitLabel}
        </Button>
      </div>
    </form>
  )
}
